import {
  CustomDataGrid,
  Loading,
  MButton,
  MCheckbox,
  ResponsiveFormContainer,
} from "@app/components";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { IsotopeResultCalculationResponseTypes } from "@app/types/samples.types";
import { Box, Grid, Modal, Typography } from "@mui/material";
import {
  useEquipment,
  useIsotoposInstalacion,
} from "@services/servicesGenericsTables";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { 
  columnsAxe, 
  columnsAxeWithClientRequest,
  columnsAxeWithClientRequestOnlyLidORBothTrue,
  columnsAxeWithClientRequestLidCharterizacionFalse,
  columnsAxeWithClientRequestOnlyCharterizacion,
  columnsAxeOnlyLidORBothTrue,
  columnsAxeLidCharterizacionFalse,
  columnsAxeOnlyCharterizacion,
  lastEquipment, 
  styleModal 
} from "./helpers";
import { useSample } from "@app/pages/samples/utils/useSample";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Document, Page, Text, View, pdf, StyleSheet } from "@react-pdf/renderer";
import { toExponentialUppercaseWhitZero } from "@app/utils/functions";
import {
  PDFHeader,
  PDFTitle,
  SampleBasicInfo,
  PreparationInfo,
  styles as pdfComponentStyles,
} from "@app/pages/samples/utils/pdf/PDFComponents";
import { GridColDef } from "@mui/x-data-grid";

const OutDataAxe = () => {
  const { getValues, setValue, watch } = useFormContext();
  const typeReport = getValues("calculation_type_report");
  const { 
    putResult, 
    fetchCalculation, 
    calculateDataOutIGAxe, 
    loadingDateAxe,
    saveIsotopeResultAxe,
    fetchIsotopeResultAxeByID,
    fetchIsotopeResultCalculationByID,
    updateRequestedByClient,
  } = useAnalysis();

  const { idMuestra } = useParams();
  const [open, setOpen] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const typeAnalysisName = getValues("typeAnalysis")?.descripcion;
  const [rows, setRows] = useState<any[]>([]);
  const { get } = useSample();
  const { equipmentList } = useEquipment();
  const { isotoposInstalacionList } = useIsotoposInstalacion();

  // Estilos personalizados con fuentes incrementadas un 25% sobre la reducción del 50%
  const pdfStyles = StyleSheet.create({
    page: {
      ...pdfComponentStyles.page,
      fontSize: 6.9,
    },
    table: {
      ...pdfComponentStyles.table,
    },
    tableRow: {
      ...pdfComponentStyles.tableRow,
    },
    tableCell: {
      ...pdfComponentStyles.tableCell,
      fontSize: 6.3,
      padding: 2.5,
    },
    tableCellLabel: {
      ...pdfComponentStyles.tableCellLabel,
      fontSize: 6.3,
      padding: 2.5,
    },
  });

  const memoFilterIsotoposInstalacion: any = useMemo(() => {
    const sample = get(Number(idMuestra));
    const filterIsotoposInstalacion =
      isotoposInstalacionList
        .filter(
          (iso) => iso.instalacion === sample?.sampling_points?.instalacion
        )
        ?.map((iso) => iso.isotopo) || [];

    const normalizedFilterIsotoposInstalacion = filterIsotoposInstalacion.map(
      (iso) => iso.replace(/-/g, '').toUpperCase()
    );
    
    const isotopesFilterWithMatch = rows.filter((row) =>
      normalizedFilterIsotoposInstalacion.includes(
        row.isotopoFile?.replace(/-/g, '').toUpperCase()
      )
    );
    return isotopesFilterWithMatch;
  }, [idMuestra, isotoposInstalacionList, rows]); // eslint-disable-line

  const handleToggleRequested = async (row: any) => {
    const newValue = row.requested_by_client ? 0 : 1;
    
    // Actualizar estado local
    setRows((prevRows) =>
      prevRows.map((r) =>
        r.id === row.id
          ? { ...r, requested_by_client: newValue === 1 }
          : r
      )
    );
    
    // Persistir en el backend usando el ID directamente
    await updateRequestedByClient(row.id, newValue);
  };

  const rowsWithHandler = useMemo(() => 
    rows.map(row => ({ ...row, onToggleRequested: handleToggleRequested })),
    [rows]
  );

  const filteredRowsWithHandler = useMemo(() => 
    memoFilterIsotoposInstalacion.map(row => ({ ...row, onToggleRequested: handleToggleRequested })),
    [memoFilterIsotoposInstalacion]
  );

  useEffect(() => {
    const fetchData = async () => {
      // Calcular datos desde archivos Area Exe
      const linkRouteFile1 = getValues("source_app_calculation_info_file");
      const linkRouteFile2 = getValues("source_app_calculation_info_file_2");
      const sample = get(Number(idMuestra));
      const inicio = dayjs(watch("end_date_time")).startOf("day");
      const fin_toma_muestra = dayjs(watch("start_date_time")).startOf("day");
      const fin = dayjs(watch("counting_date")).startOf("day");
      let diffDate = fin.diff(inicio, "minute");
      if (
        sample?.sample_type.codigo == "PP" &&
        ( sample?.accumulation.code === "S" || sample?.accumulation.code === "T" )
      ) {
        // Excel: =(C29-((C31+C30)/2))*24*60
        // C29 = fin (counting_date), C31 = inicio (end_date_time), C30 = fin_toma_muestra (start_date_time)
        const promedioTimestamp = (inicio.valueOf() + fin_toma_muestra.valueOf()) / 2;
        const fechaIntermedia = dayjs(promedioTimestamp);
        const diffDays = (fin.valueOf() - fechaIntermedia.valueOf()) / (1000 * 60 * 60 * 24);
        diffDate = diffDays * 24 * 60;
      }

      setValue("decaimiento_minutos", diffDate);
      
      const calculationEquipment = getValues("calculation_equipment");

      const cod_instalacion = sample?.installation?.codigo || "";
      const ref_cliente = getValues("client_reference") || "";

      const response = await calculateDataOutIGAxe(
        linkRouteFile1,
        linkRouteFile2,
        calculationEquipment,
        getValues("counting_date"),
        getValues("calculation_self_absorption_factor"),
        getValues("calculation_performance"),
        getValues("calculation_amount_sample"),
        getValues("calculation_geometric_factor"),
        diffDate,
        cod_instalacion,
        ref_cliente
      );
      const calculatedData = response?.filter((row) => row != null) || [];
      
      // Establecer requested_by_client a true por defecto antes de guardar
      const calculatedDataWithDefault = calculatedData.map(row => ({
        ...row,
        requested_by_client: true
      }));
      
      // Guardar los datos calculados en la base de datos (igual que hace Genie)
      await saveIsotopeResultAxe(calculatedDataWithDefault);
      
      // Recargar los datos guardados para obtener los IDs
      const savedData = await fetchIsotopeResultCalculationByID();
      
      // Mezclar datos calculados con datos guardados para obtener IDs
      const mergedData = calculatedData.map((calcRow) => {
        const savedRow = savedData?.find(
          (saved) => saved.isotope?.codigo?.toUpperCase() === calcRow.isotopoFile?.toUpperCase()
        );
        
        if (savedRow) {
          return {
            ...calcRow,
            id: savedRow.id,
            requested_by_client: savedRow.requested_by_client ? true : false,
          };
        }
        
        return calcRow;
      });
      
      setRows(mergedData);
    };
    if (open) fetchData();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const preloadResults = async () => {
      const idResultPreload = getValues("idResultPreload");
      if (idResultPreload !== undefined) {
        const response = await fetchCalculation(idResultPreload);
        if (response) {
          setValue("to_report", response.to_reports ? "to_report" : "");
        }
      }
    };
    preloadResults();
  }, [getValues("idResultPreload")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToReportUpdate = () => {
    if (getValues("idResultPreload")) {
      putResult(getValues("idResultPreload"), true);
      setValue("to_report", "to_report");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = async () => {
    setOpen(true);
  };

  const getColumnsForTable = () => {
    let table: GridColDef[] = [];
    // Para "Isótopos detectados" mostrar todos los datos sin filtrar por only_lid
    if (typeReport?.includes("report_characterization")) {
      table = columnsAxeOnlyCharterizacion();
    } else {
      table = columnsAxeLidCharterizacionFalse;
    }
    return table;
  };

  const getColumnsForClientRequestTable = () => {
    let table: GridColDef[] = [];
    if (
      typeReport?.includes("report_characterization") &&
      typeReport?.includes("only_lid")
    ) {
      table = columnsAxeWithClientRequestOnlyLidORBothTrue;
    } else if (
      !typeReport?.includes("report_characterization") &&
      !typeReport?.includes("only_lid")
    ) {
      table = columnsAxeWithClientRequestLidCharterizacionFalse;
    } else if (
      typeReport?.includes("report_characterization") &&
      !typeReport?.includes("only_lid")
    ) {
      table = columnsAxeWithClientRequestOnlyCharterizacion();
    } else if (
      !typeReport?.includes("report_characterization") &&
      typeReport?.includes("only_lid")
    ) {
      table = columnsAxeWithClientRequestOnlyLidORBothTrue;
    }
    return table;
  };

  const generatePDFDocument = () => {
    const title = `Datos de salida - ${typeAnalysisName || ""}`;
    const currentDate = new Date().toLocaleDateString();
    const sample = get(Number(idMuestra));
    
    // Determinar el tipo específico de análisis IG (Exe o Genie)
    const sourceApp = getValues("source_app_calculation_info") || getValues("source_app_calculation_info_previus");
    const analysisTypeCode = sourceApp === "GENIE" ? "IG-GENIE" : "IG-EXE";
    
    // Combinar datos de la muestra con los datos del formulario
    const dataObjects = {
      ...sample,
      typeAnalysis: { ...getValues("typeAnalysis"), codigo: analysisTypeCode },
      calculation_analyst: getValues("calculation_analyst"),
      calculation_equipment: getValues("calculation_equipment"),
      calculation_detector: getValues("detector_file"),
      counting_date: getValues("calculation_date_countig_file_exe") || getValues("counting_date"),
      date_start_sampling: getValues("start_date_time"),
      date_end_sampling: getValues("end_date_time"),
      calculation_amount_sample: getValues("calculation_amount_sample"),
      total_time_counting: getValues("first_counting_time") || getValues("calculation_time_count"),
      geometry: getValues("calculation_equipment"),
      height: getValues("geometry_height"),
      decay_minutes: getValues("decaimiento_minutos"),
      wet_dry_weight_ratio: getValues("calculation_wet_dry_weight_ratio"),
      geometric_factor: getValues("calculation_geometric_factor"),
      self_absorption_factor: getValues("calculation_self_absorption_factor"),
      counts_351kev: getValues("pico_counts_341"),
      counts_911kev: getValues("pico_counts_911"),
      laboratory: getValues("laboratory"),
      calculation_units: getValues("calculation_units"),
      preparation_procedure: getValues("preparation_procedure"),
      calculation_tsie_sample: getValues("calculation_tsie_sample"),
    };

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <PDFHeader currentDate={currentDate} dataObjects={dataObjects} />
          <PDFTitle maslReference={dataObjects?.masl_reference || ""} analysisDescription={typeAnalysisName || ""} />
          <SampleBasicInfo dataObjects={dataObjects} />
          <PreparationInfo dataObjects={dataObjects} />
          <Text style={{ fontSize: 7.5, fontWeight: "bold", marginTop: 4, marginBottom: 4 }}>{title}</Text>

          <Text style={{ fontSize: 6.3, fontWeight: "bold", marginBottom: 4 }}>Isótopos detectados</Text>
          <View style={[pdfStyles.tableRow, { backgroundColor: "#f0f0f0", fontWeight: "bold" }]}> 
            {getColumnsForTable().map((col, index) => (
              <Text key={index} style={index === 0 ? pdfStyles.tableCellLabel : pdfStyles.tableCell}>
                {col.headerName}
              </Text>
            ))}
          </View>

          {rows.map((row, rowIndex) => {
            // Para "Isótopos detectados" mostrar todos los datos sin filtrar
            return (
              <View key={rowIndex} style={pdfStyles.tableRow}>
                <Text style={pdfStyles.tableCellLabel}>
                  {row.isotopoFile || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.energy || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.cuenta || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.efficiency || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.error || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {toExponentialUppercaseWhitZero(row.actividad, 2)}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {toExponentialUppercaseWhitZero(row.incertidumbre, 2)}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {toExponentialUppercaseWhitZero(row.lid, 2)}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.fondo_integration_to_apply || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.fondo_area_to_apply || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.fondo_area_error_to_apply || ""}
                </Text>
              </View>
            );
          })}

          <Text style={{ fontSize: 6.3, fontWeight: "bold", marginTop: 8, marginBottom: 4 }}>Isótopos solicitados por el cliente</Text>
          <View style={[pdfStyles.tableRow, { backgroundColor: "#f0f0f0", fontWeight: "bold" }]}> 
            {getColumnsForTable().map((col, index) => (
              <Text key={index} style={index === 0 ? pdfStyles.tableCellLabel : pdfStyles.tableCell}>
                {col.headerName}
              </Text>
            ))}
          </View>

          {memoFilterIsotoposInstalacion.filter(row => row.requested_by_client).map((row, rowIndex) => {
            const activity = parseFloat(row.actividad);
            const lid = parseFloat(row.lid);
            const uncertainty = parseFloat(row.incertidumbre);
            const shouldHideActivity = typeReport?.includes("only_lid") &&
                                       activity <= lid && activity <= uncertainty;
            
            return (
              <View key={rowIndex} style={pdfStyles.tableRow}>
                <Text style={pdfStyles.tableCellLabel}>
                  {row.isotopoFile || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.energy || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.cuenta || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.efficiency || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.error || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {shouldHideActivity ? "" : toExponentialUppercaseWhitZero(row.actividad, 2)}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {shouldHideActivity || !row.incertidumbre ? "" : toExponentialUppercaseWhitZero(row.incertidumbre, 2)}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {toExponentialUppercaseWhitZero(row.lid, 2)}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.fondo_integration_to_apply || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.fondo_area_to_apply || ""}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {row.fondo_area_error_to_apply || ""}
                </Text>
              </View>
            );
          })}
        </Page>
      </Document>
    );
  };

  const handleDownloadPDF = async () => {
    const title = `Datos_de_salida_${typeAnalysisName || ""}`;
    const pdfDoc = generatePDFDocument();
    const blob = await pdf(pdfDoc).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/\s+/g, "_")}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {getValues("idResultPreload") && (
        <>
          <ResponsiveFormContainer columns={1} ml={0.5}>
            <Box display="flex" justifyContent="center" mt={2}>
              <MCheckbox
                key={JSON.stringify(watch("to_report"))}
                name="to_report"
                options={[
                  {
                    label: "Para informe",
                    value: "to_report",
                  },
                ]}
                multiple={false}
                disabled={true}
              />
              <MButton
                variant="contained"
                color="primary"
                text="Utilizar resultado en informe"
                onClick={handleToReportUpdate}
                disabled={getValues("to_report") === "to_report"}
              />
            </Box>
          </ResponsiveFormContainer>
          <Grid
            item
            xs={12}
            md={12}
            display="flex"
            justifyContent="flex-end"
            gap={2}
            mt={2}
          >
            <MButton
              variant="outlined"
              color="secondary"
              text=" Datos de salida 1"
              onClick={() => handleOpen()}
            />
          </Grid>

          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <>
              {loadingDateAxe && <Loading />}
              <Box sx={styleModal} ref={modalContentRef}>
                <Typography
                  id="modal-title-IG"
                  variant="h6"
                  component="h2"
                  sx={{ mb: 2, borderBottom: 3, borderColor: "divider" }}
                >
                  Datos de salida {typeAnalysisName && ` - ${typeAnalysisName}`}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                      Isótopos detectados
                    </Typography>
                    <CustomDataGrid
                      key={"data-input-isotopeResultCalculationAxe"}
                      rows={rowsWithHandler || []}
                      columns={getColumnsForTable()}
                      search={false}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                      Isótopos solicitados por el cliente
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
                      Marque los isótopos que desea incluir en el informe
                    </Typography>
                    <CustomDataGrid
                      key={"data-input-isotopeResultCalculationAxe-client"}
                      rows={filteredRowsWithHandler || []}
                      columns={getColumnsForClientRequestTable()}
                      search={false}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 2,
                    gap: 2,
                  }}
                >
                  <MButton
                    onClick={handleDownloadPDF}
                    text="Descargar PDF"
                    variant="outlined"
                    color="primary"
                    disabled={
                      !rows || 
                      rows.length === 0 || 
                      !memoFilterIsotoposInstalacion.some(row => row.requested_by_client)
                    }
                  />
                  <MButton
                    onClick={handleClose}
                    text="Cerrar"
                    variant="contained"
                  />
                </Box>
              </Box>
            </>
          </Modal>
        </>
      )}
    </>
  );
};

export default OutDataAxe;
